import React, { useState, useEffect } from "react"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { NavLink } from "react-router-dom"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./margin-attribution.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import Badge from "../../components/ui/badge/badge"

// Build up a list of years for the select field
let yearsObj = {}
let nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

// Returns a dashboard for viewing the margins value associated with each channel
export default function MarginAttribution() {
	const [loading, setLoading] = useState(false)
	const [bookings, setBookings] = useState([])
	const [attributionSummary, setAttributionSummary] = useState({})
	const [clientBookingCount, setClientBookingCount] = useState({})

	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")

	// Add new useEffect to calculate attribution summary when bookings change
	useEffect(() => {
		if (bookings.length === 0) return

		const summary = bookings.reduce((acc, booking) => {
			// Map a few of the sources into different names
			let baseSource = "organic"
			if (booking.tracking?.utm_source && booking.tracking?.utm_source !== "Golf Holidays Direct") {
				baseSource = booking.tracking?.utm_source === "bing_ads" ? "bing" : booking.tracking?.utm_source
			} else if (booking.tracking?.utm_source === "Golf Holidays Direct") {
				baseSource = "newsletter"
			} else if (booking.enquiry?.site === "SYSTEM") {
				baseSource = "system"
			}

			// Set the campaign variable
			let baseCampaign = "No Campaign"
			if (booking.tracking?.utm_campaign) {
				baseCampaign = booking.tracking?.utm_campaign
			} else if (booking.enquiry?.site === "SYSTEM" && !booking.enquiry?.inbound_channel) {
				// Check if this is a repeat booking using the client booking count
				const isRepeat = booking.client && clientBookingCount[booking.client] > 1
				baseCampaign = isRepeat ? "Repeats" : "Freephone & Others"
			} else if (booking.enquiry?.inbound_channel) {
				if (booking.enquiry?.inbound_channel === "EMAIL" && !booking.enquiry?.utm_source) {
					baseCampaign = "Sales@ Email"
				} else if (booking.enquiry?.inbound_channel === "EMAIL" && booking.enquiry?.utm_source) {
					baseSource = booking.tracking?.utm_source === "bing_ads" ? "bing" : booking.tracking?.utm_source
					baseCampaign = "Sales@ Email"
				} else if (booking.enquiry?.inbound_channel === "EMAIL_MARKETING") {
					baseSource = "newsletter"
					baseCampaign = "Marketing@ Email"
				} else if (booking.enquiry?.inbound_channel === "FACEBOOK") {
					baseSource = "socials"
					baseCampaign = "Facebook Messenger"
				} else if (booking.enquiry?.inbound_channel === "INSTAGRAM") {
					baseSource = "socials"
					baseCampaign = "Instagram DM"
				}
			}

			if (!acc[baseSource]) {
				acc[baseSource] = {
					totalProfit: 0,
					totalRevenue: 0,
					campaigns: {},
				}
			}

			if (!acc[baseSource].campaigns[baseCampaign]) {
				acc[baseSource].campaigns[baseCampaign] = {
					totalProfit: 0,
					totalRevenue: 0,
					bookingCount: 0,
				}
			}

			acc[baseSource].totalProfit += booking?.margins?.profit || 0
			acc[baseSource].totalRevenue += booking?.margins?.customer_price || 0
			acc[baseSource].campaigns[baseCampaign].totalProfit += booking?.margins?.profit || 0
			acc[baseSource].campaigns[baseCampaign].totalRevenue += booking?.margins?.customer_price || 0
			acc[baseSource].campaigns[baseCampaign].bookingCount += 1

			return acc
		}, {})

		setAttributionSummary(summary)
	}, [bookings, clientBookingCount])

	const findBookings = async () => {
		// Reset the state
		setLoading(true)

		// Get the milliseconds timestamps from the chosen filters
		let fetchStart = 0
		let fetchEnd = 0
		if (month && year) {
			fetchStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
			fetchEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
		} else if (!month && year) {
			fetchStart = moment(`${year}`, "YYYY").startOf("year").valueOf()
			fetchEnd = moment(`${year}`, "YYYY").endOf("year").valueOf()
		}
		const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
		const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)

		try {
			// Find all the bookings where the created date is within the range
			const bookingsSnapshot = await db.collection("bookings").where("created", ">=", startTimestamp).where("created", "<=", endTimestamp).get()

			// Collect all unique agent IDs and client IDs
			const agentIds = new Set()
			const clientIds = new Set()
			const bookingsData = []

			bookingsSnapshot.forEach((booking) => {
				const bookingData = booking.data()
				if (bookingData.agent) {
					agentIds.add(bookingData.agent)
				}
				if (bookingData.client) {
					clientIds.add(bookingData.client)
				}
				bookingsData.push({
					bookingID: booking.id,
					...bookingData,
				})
			})

			// Fetch all agents in parallel
			const agentsMap = new Map()
			const agentPromises = Array.from(agentIds).map(async (agentId) => {
				const agentDoc = await db.collection("users").doc(agentId).get()
				if (agentDoc.exists) {
					agentsMap.set(agentId, {
						id: agentDoc.id,
						...agentDoc.data(),
					})
				}
			})
			await Promise.all(agentPromises)

			// Build a timestamp for 3 months ago
			const threeMonthsBack = moment().subtract(3, "months").valueOf()
			const threeMonthsBackTimestamp = firebase.firestore.Timestamp.fromMillis(threeMonthsBack)

			// Fetch all removed enquiries for all clients in parallel
			const removedEnquiriesMap = new Map()
			const enquiryPromises = Array.from(clientIds).map(async (clientId) => {
				const enquiryDocs = await db.collection("enquiries_removed").where("enquiry.client", "==", clientId).where("enquiry.created", ">=", threeMonthsBackTimestamp).get()

				const clientEnquiries = []
				enquiryDocs.forEach((enquiry) => {
					clientEnquiries.push({
						...enquiry.data()?.enquiry,
						enquiryID: enquiry.id,
					})
				})
				removedEnquiriesMap.set(clientId, clientEnquiries)
			})
			await Promise.all(enquiryPromises)

			// Fetch client booking counts in parallel
			const clientBookingCount = {}
			const clientCountPromises = Array.from(clientIds).map(async (clientId) => {
				const clientBookings = await db.collection("bookings").where("client", "==", clientId).get()
				clientBookingCount[clientId] = clientBookings.size
			})
			await Promise.all(clientCountPromises)

			// Now process all bookings with the cached data
			const bookingsArray = bookingsData.map((bookingData) => {
				// Get agent data from cache
				const agentData = bookingData.agent ? agentsMap.get(bookingData.agent) || {} : {}

				// Get enquiry data from cache
				const clientEnquiries = removedEnquiriesMap.get(bookingData.client) || []
				let enquiryData = null
				let trackingData = null

				// Find the matching enquiry by created timestamp
				if (bookingData.enquiry?.created) {
					const matchingEnquiry = clientEnquiries.find((enq) => enq.created?.seconds === bookingData.enquiry?.created?.seconds)

					if (matchingEnquiry) {
						enquiryData = matchingEnquiry
						if (matchingEnquiry.tracking) {
							trackingData = matchingEnquiry.tracking
						}
					}
				}

				// If there are multiple enquiries and this is a SYSTEM enquiry, try to find one with tracking
				if (clientEnquiries.length > 1 && enquiryData?.site === "SYSTEM") {
					const altEnquiryWithTracking = clientEnquiries.find((enquiry) => enquiry.tracking)
					if (altEnquiryWithTracking) {
						trackingData = altEnquiryWithTracking.tracking
						enquiryData = altEnquiryWithTracking
					}
				}

				// Fall back to the booking's own data when no match was found in enquiries_removed
				if (!trackingData && bookingData.tracking) {
					trackingData = bookingData.tracking
				}
				if (!trackingData && bookingData.enquiry?.tracking) {
					trackingData = bookingData.enquiry.tracking
				}
				if (!enquiryData && bookingData.enquiry) {
					enquiryData = bookingData.enquiry
				}

				// Format both the customer price and profit to 2 decimal places
				const customerPriceFormatted = new Intl.NumberFormat("en-GB", {
					style: "currency",
					currency: "GBP",
				}).format(bookingData.margins?.customer_price)
				const profitFormatted = new Intl.NumberFormat("en-GB", {
					style: "currency",
					currency: "GBP",
				}).format(bookingData.margins?.profit)

				// Return a single object combining everything we care about
				return {
					...bookingData,
					agent: agentData,
					enquiry: enquiryData,
					tracking: trackingData,
					margins: {
						customer_price: bookingData.margins?.customer_price,
						customer_price_formatted: customerPriceFormatted,
						profit: bookingData.margins?.profit,
						profit_formatted: profitFormatted,
					},
				}
			})

			// Set into state
			setBookings(bookingsArray)
			setClientBookingCount(clientBookingCount)
		} catch (err) {
			console.log(err)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Margin Attribution</h1>
			</Title>

			<div className="page-filters extra-stats">
				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						disabled={(!month || !year) && !year}
						label="Fetch stats"
						loadingText="Fetching stats..."
						loading={loading}
						onClick={() => findBookings()}
					/>
				</div>
			</div>

			<Table
				className="travellers-table"
				headings={["Source", "Source Total", "Campaigns"]}
				noResults={Object.keys(attributionSummary).length === 0}
				noResultsMessage={"No results matching those filters"}>
				{Object.entries(attributionSummary)
					.sort(([, a], [, b]) => b.totalProfit - a.totalProfit)
					.map(([source, data]) => (
						<tr key={source}>
							<td>{source}</td>
							<td>{new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(data.totalProfit)}</td>
							<td>
								<ul className="medium-list">
									{Object.entries(data.campaigns)
										.sort(([, a], [, b]) => b.totalProfit - a.totalProfit)
										.map(([campaign, stats], index) => (
											<li key={`${source}-${campaign}`}>
												{campaign} <small>({new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(stats.totalProfit)})</small>
											</li>
										))}
								</ul>
							</td>
						</tr>
					))}
				{Object.keys(attributionSummary).length > 0 &&
					(() => {
						const totalAll = Object.values(attributionSummary).reduce((total, data) => total + data.totalProfit, 0)

						const totals = [
							{
								label: "Total (system)",
								sources: ["system"],
								total: Object.entries(attributionSummary)
									.filter(([source]) => ["system"].includes(source))
									.reduce((total, [_, data]) => total + data.totalProfit, 0),
							},
							{
								label: "Total (paid ads/crm/socials)",
								sources: ["google", "bing", "facebook", "newsletter", "socials"],
								total: Object.entries(attributionSummary)
									.filter(([source]) => ["google", "bing", "facebook", "newsletter", "socials"].includes(source))
									.reduce((total, [_, data]) => total + data.totalProfit, 0),
							},
							{
								label: "Total (organic)",
								sources: ["organic"],
								total: Object.entries(attributionSummary)
									.filter(([source]) => ["organic"].includes(source))
									.reduce((total, [_, data]) => total + data.totalProfit, 0),
							},
						]
							.map((item) => ({
								...item,
								percentage: (item.total / totalAll) * 100,
							}))
							.sort((a, b) => b.percentage - a.percentage)

						return totals.map(({ label, total, percentage }) => (
							<tr
								key={label}
								className="totals-row">
								<td>
									<strong>{label}</strong>
								</td>
								<td>{`${new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(total)} (${percentage.toFixed(1)}%)`}</td>
								<td></td>
							</tr>
						))
					})()}
			</Table>

			<br />
			<br />
			<br />

			<Table
				className="travellers-table"
				headings={["Site", "Booked", "Agent", "Has Tracking", "UTM Source", "UTM Campaign", "UTM Medium", "UTM Content", "UTM Term", "Customer Price", "Sales Margin", ""]}
				noResults={Object.entries(bookings).length === 0}
				noResultsMessage={"No results matching those filters"}>
				{bookings.map((booking) => (
					<tr
						key={booking.bookingID}
						data-booking-id={booking.bookingID}>
						<td>{booking.enquiry?.site === "SYSTEM" && booking.client && clientBookingCount[booking.client] > 1 ? "SYSTEM-REPEAT" : booking.enquiry?.site || "GHD-DIRECT"}</td>
						<td>{moment(booking.created?.seconds, "X").format("MMM D[,] YYYY")}</td>
						<td>
							{booking?.agent?.first_name} {booking?.agent?.last_name}
						</td>
						<td>
							{booking.tracking?.utm_source && (
								<Badge
									label="Yes"
									type="POSITIVE"
								/>
							)}

							{!booking.tracking?.utm_source && (
								<Badge
									label="No"
									type="NEGATIVE"
								/>
							)}
						</td>
						<td>{booking?.tracking?.utm_source || <small className="no-value">-</small>}</td>
						<td>{booking?.tracking?.utm_campaign || <small className="no-value">-</small>}</td>
						<td>{booking?.tracking?.utm_medium || <small className="no-value">-</small>}</td>
						<td>{booking?.tracking?.utm_content || <small className="no-value">-</small>}</td>
						<td>{booking?.tracking?.utm_term || <small className="no-value">-</small>}</td>
						<td>{booking?.margins?.customer_price_formatted}</td>
						<td>{booking?.margins?.profit_formatted}</td>
						<td className="is-button">
							<NavLink to={`booking/${booking.bookingID}`}>View booking</NavLink>
							<a
								target="_blank"
								rel="noreferrer"
								href={`booking/${booking.bookingID}`}>
								View in new tab
							</a>
						</td>
					</tr>
				))}
			</Table>
		</Tile>
	)
}
